import { useEffect, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Button } from '@nesso/ui/primitives/button';
import { RadioGroup } from '@nesso/ui/primitives/radio-group';
import { RadioGroupItemField } from '@nesso/ui/primitives/radio-group-item-field';

import {
    SUPERBILL_LOCATIONS,
    type SuperbillLocation,
    useUpdateSuperbillSettingsOptions,
} from '@/entities/client-billing-settings';

import { formatSuperbillLocationLabel } from '../../../../lib/format-superbill-location-label';

type Props = {
    storedLocation: SuperbillLocation;
    defaultLocationName: string;
    onSaved: () => void;
    onPendingChange: (pending: boolean) => void;
};

export function DisplayAddressEditor({
    storedLocation,
    defaultLocationName,
    onSaved,
    onPendingChange,
}: Props) {
    const [selection, setSelection] = useState(storedLocation);
    const mutation = useMutation(useUpdateSuperbillSettingsOptions());

    useEffect(() => {
        onPendingChange(mutation.isPending);
        return () => onPendingChange(false);
    }, [mutation.isPending, onPendingChange]);

    const handleSave = () => {
        if (selection === storedLocation) {
            onSaved();
            return;
        }

        mutation.mutate(
            { location: selection },
            {
                onSuccess: onSaved,
                onError: () => toast.error('Unable to update the display address. Try again.'),
            },
        );
    };

    return (
        <>
            <RadioGroup
                value={selection}
                onValueChange={(value) => setSelection(value as SuperbillLocation)}
                aria-label="Display address"
            >
                {SUPERBILL_LOCATIONS.map((location) => (
                    <RadioGroupItemField
                        key={location}
                        value={location}
                        label={formatSuperbillLocationLabel(location, defaultLocationName)}
                    />
                ))}
            </RadioGroup>
            <div>
                <Button type="button" size="lg" disabled={mutation.isPending} onClick={handleSave}>
                    Save
                </Button>
            </div>
        </>
    );
}
